import { FieldGroup, OptionsConfig } from "@app/projects/create/interfaces";
import { ProjectExclusionType } from "@app/projects/enumerators";
import { isNullOrUndefined } from "@app/shared/utility";
import { Roles } from "@app/user";
import DisableUnsupportedBulkEditOptions from "../../edit/DisableUnsupportedOption"

function getOptions<T>(
  configParam: OptionsConfig<T>
): Array<FieldGroup<T>> {
  const defaultOptions: OptionsConfig<T> = {
    accuracyAndAdequacy: false,
    checkboxRefs: undefined,
    disableUnsupportedOptions: false,
    displayOnNetworkAccordion: false,
    enablePatternOfCareProvidersOption: false,
    enableRegulatoryExceptionReportsOption: false,
    hideCMSApplicationCredit: true,
    hideCareJourneyScores: true,
    hideClosestProviderReport: false,
    hideCreateStandards: true,
    hideEnterpriseComplianceDashboard: true,
    hideMotiveAppropriatenessScore: true,
    hidePciData: true,
    hidePracAtLocCondifence: true,
    hideProviderClaimsActivity: true,
    hideStrictTimeDistance: true,
    isZipProject: false,
    showAffiliationsInsights: false,
    showNetworkIntegrityDashboard: false,
    showPciDataChecked: false,
    useAccuracy: false,
    userRoles: []
  };

  const optionsConfig: OptionsConfig<T> = {
    accuracyAndAdequacy: isNullOrUndefined(configParam.accuracyAndAdequacy) ? defaultOptions.accuracyAndAdequacy : configParam.accuracyAndAdequacy,
    checkboxRefs: configParam.checkboxRefs,
    disableUnsupportedOptions: isNullOrUndefined(configParam.disableUnsupportedOptions) ? defaultOptions.disableUnsupportedOptions : configParam.disableUnsupportedOptions,
    displayOnNetworkAccordion: isNullOrUndefined(configParam.displayOnNetworkAccordion) ? defaultOptions.displayOnNetworkAccordion : configParam.displayOnNetworkAccordion,
    enablePatternOfCareProvidersOption: isNullOrUndefined(configParam.enablePatternOfCareProvidersOption) ? defaultOptions.enablePatternOfCareProvidersOption : configParam.enablePatternOfCareProvidersOption,
    enableRegulatoryExceptionReportsOption: isNullOrUndefined(configParam.enableRegulatoryExceptionReportsOption) ? defaultOptions.enableRegulatoryExceptionReportsOption : configParam.enableRegulatoryExceptionReportsOption,
    hideCMSApplicationCredit: isNullOrUndefined(configParam.hideCMSApplicationCredit) ? defaultOptions.hideCMSApplicationCredit : configParam.hideCMSApplicationCredit,
    hideCareJourneyScores: isNullOrUndefined(configParam.hideCareJourneyScores) ? defaultOptions.hideCareJourneyScores : configParam.hideCareJourneyScores,
    hideClosestProviderReport: isNullOrUndefined(configParam.hideClosestProviderReport) ? defaultOptions.hideClosestProviderReport : configParam.hideClosestProviderReport,
    hideCreateStandards: isNullOrUndefined(configParam.hideCreateStandards) ? defaultOptions.hideCreateStandards : configParam.hideCreateStandards,
    hideEnterpriseComplianceDashboard: isNullOrUndefined(configParam.hideEnterpriseComplianceDashboard) ? defaultOptions.hideEnterpriseComplianceDashboard : configParam.hideEnterpriseComplianceDashboard,
    hideMotiveAppropriatenessScore: isNullOrUndefined(configParam.hideMotiveAppropriatenessScore) ? defaultOptions.hideMotiveAppropriatenessScore : configParam.hideMotiveAppropriatenessScore,
    hidePciData: isNullOrUndefined(configParam.hidePciData) ? defaultOptions.hidePciData : configParam.hidePciData,
    hidePracAtLocCondifence: isNullOrUndefined(configParam.hidePracAtLocCondifence) ? defaultOptions.hidePracAtLocCondifence : configParam.hidePracAtLocCondifence,
    hideProviderClaimsActivity: isNullOrUndefined(configParam.hideProviderClaimsActivity) ? defaultOptions.hideProviderClaimsActivity : configParam.hideProviderClaimsActivity,
    hideStrictTimeDistance: isNullOrUndefined(configParam.hideStrictTimeDistance) ? defaultOptions.hideStrictTimeDistance : configParam.hideStrictTimeDistance,
    isZipProject: isNullOrUndefined(configParam.isZipProject) ? defaultOptions.isZipProject : configParam.isZipProject,
    showAffiliationsInsights: isNullOrUndefined(configParam.showAffiliationsInsights) ? defaultOptions.showAffiliationsInsights : configParam.showAffiliationsInsights,
    showNetworkIntegrityDashboard: isNullOrUndefined(configParam.showNetworkIntegrityDashboard) ? defaultOptions.showNetworkIntegrityDashboard : configParam.showNetworkIntegrityDashboard,
    showPciDataChecked: isNullOrUndefined(configParam.showPciDataChecked) ? defaultOptions.showPciDataChecked : configParam.showPciDataChecked,
    useAccuracy: isNullOrUndefined(configParam.useAccuracy) ? defaultOptions.useAccuracy : configParam.useAccuracy,
    userRoles: [].concat((configParam.userRoles || defaultOptions.userRoles)),
  };

  const allOptions: Array<FieldGroup<T>> = [
    {
      fields: [
        {
          disabled: optionsConfig.isZipProject,
          hidden: optionsConfig.hideStrictTimeDistance,
          key: "strictTimeAndDistance" as keyof T,
          title: "Use Strict Evaluation for \"Time and Distance\" Analysis",
          tooltip: "When unselected, providers cover members if they cover by either time or distance and another provider covers the member by the other measure. When selected, providers must cover by both time and distance.",
        },
        {
          hidden: optionsConfig.isZipProject,
          key: "autoArchiveUnarchiveSpecialtyExceptions" as keyof T,
          title: "Auto Archive/Un-Archive Specialty Exceptions",
        },
        {
          disabled: optionsConfig.isZipProject,
          key: "excludeInVolumeActions" as keyof T,
          title: "Exclude in Volume Actions"
        },
        {
          hidden: optionsConfig.isZipProject || optionsConfig.hideEnterpriseComplianceDashboard,
          key: "includeInEnterpriseComplianceDashboard" as keyof T,
          rolesAllowed: [Roles.ENTERPRISE_COMPLIANCE_DASHBOARD_USER],
          title: "Include In Enterprise Compliance Dashboard"
        },
        {
          hidden: optionsConfig.isZipProject || !optionsConfig.accuracyAndAdequacy,
          key: "showAccuracyGapAnalysis" as keyof T,
          rolesAllowed: [Roles.ACCURACY_LICENSE_TIER_1],
          rolesDenied: [Roles.TOKEN_USER],
          title: "Show Accuracy Gap Analysis"
        },
        {
          hidden: optionsConfig.isZipProject || optionsConfig.hideCMSApplicationCredit,
          key: "showCMSCredit" as keyof T,
          rolesAllowed: [Roles.CMS_APPLICATION_CREDIT_VIEWER],
          title: "Apply CMS Application Credit",
        },
        {
          disabled: !optionsConfig.useAccuracy,
          hidden: optionsConfig.hidePracAtLocCondifence || optionsConfig.isZipProject,
          key: "excludeAccuracyPractitionerAtLocationInspector" as keyof T,
          title: "Exclude Accuracy Practitioner At Location Confidence Inspector",
        },
        {
          disabled: !optionsConfig.useAccuracy,
          hidden: optionsConfig.hideProviderClaimsActivity || optionsConfig.isZipProject,
          key: "excludeProviderClaimsActivityInspector" as keyof T,
          title: "Exclude Accuracy Provider Claims Activity Inspector",
        },
        {
          hidden: optionsConfig.hideCreateStandards,
          key: "calculateStandards" as keyof T,
          title: "Create Template Standards",
        }
      ],
      title: "Analysis",
    },
    {
      fields: [
        {
          hidden: optionsConfig.isZipProject,
          key: "includeGapAging" as keyof T,
          title: "Show Gap Aging Analysis",
        },
        {
          disabled: !optionsConfig.useAccuracy,
          hidden: optionsConfig.isZipProject,
          key: "showAttestation" as keyof T,
          title: "Show Attestation",
          tooltip: !optionsConfig.useAccuracy ? "Only available on projects that use an Accuracy template" : null,
        },
        {
          key: "showNppesDates" as keyof T,
          title: "Show NPPES Dates",
        },
        {
          hidden: optionsConfig.isZipProject,
          key: "showAdvancedComparison" as keyof T,
          title: "Show Advanced Comparison Features",
        },
        {
          key: "showAverageDistance" as keyof T,
          title: "Show Average Distance",
        },
        {
          key: "showMaximumDistance" as keyof T,
          title: "Show Maximum Distance",
        },
        {
          key: "showAverageTime" as keyof T,
          title: "Show Average Time",
        },
        {
          key: "showMaximumTime" as keyof T,
          title: "Show Maximum Time",
        },
        {
          hidden: optionsConfig.isZipProject,
          key: "hasZipSummaries" as keyof T,
          title: "Show Zip Summaries",
        },
        {
          hidden: optionsConfig.isZipProject,
          key: "showNextClosestNetworkProviders" as keyof T,
          title: "Show Next Closest Network Providers",
        },
        {
          disabled: !optionsConfig.enablePatternOfCareProvidersOption,
          hidden: optionsConfig.isZipProject,
          key: "showPatternOfCareProviders" as keyof T,
          rolesAllowed: [Roles.PATTERN_OF_CARE_USER],
          title: "Show Pattern Of Care Providers",
        },
        {
          disabled: !optionsConfig.enableRegulatoryExceptionReportsOption,
          hidden: optionsConfig.isZipProject,
          key: "enableRegulatoryExceptionReports" as keyof T,
          title: "Enable Regulatory Exception Reports",
        },
        {
          hidden: optionsConfig.isZipProject || optionsConfig.hideClosestProviderReport,
          key: "showClosestProviderReport" as keyof T,
          title: "Show Closest Provider Report",
        },
        {
          hidden: optionsConfig.isZipProject,
          key: "showMipsScore" as keyof T,
          rolesAllowed: [Roles.QUALITY_SCORES],
          title: "Show MIPS Score",
        },
        {
          hidden: optionsConfig.hidePciData,
          key: "showPciData" as keyof T,
          rolesAllowed: [Roles.PROVIDER_CLAIMS_INSIGHTS],
          title: "Show Provider Claims Insights Data",
        },
        {
          dependency: "showPciData",
          hidden: optionsConfig.hidePciData || !optionsConfig.showPciDataChecked,
          key: "showVaClaimsVolumeColumn" as keyof T,
          rolesAllowed: [Roles.PROVIDER_CLAIMS_INSIGHTS],
          title: "Show VA Claims Volume Column",
        },
        {
          dependency: "showPciData",
          hidden: optionsConfig.hidePciData || !optionsConfig.showPciDataChecked,
          key: "showTricareClaimsVolumeColumn" as keyof T,
          rolesAllowed: [Roles.PROVIDER_CLAIMS_INSIGHTS],
          title: "Show TRICARE Claims Volume Column",
        },
        {
          dependency: "showPciData",
          hidden: optionsConfig.isZipProject || optionsConfig.hidePciData || !optionsConfig.showPciDataChecked,
          key: "showPciDashboard" as keyof T,
          rolesAllowed: [Roles.PROVIDER_CLAIMS_INSIGHTS],
          title: "Show Provider Claims Insights Dashboard",
        },
        {
          dependency: "showPciData",
          hidden: optionsConfig.hidePciData || !optionsConfig.showPciDataChecked,
          key: "showLocationConfidenceScores" as keyof T,
          rolesAllowed: [Roles.QUEST_USER, Roles.QUEST_ADMIN, Roles.CLIENT_ADMIN],
          title: "Show Location Confidence Score",
        },
        {
          dependency: "showPciData",
          hidden: optionsConfig.isZipProject || optionsConfig.hidePciData ||
            !optionsConfig.showPciDataChecked || optionsConfig.hideMotiveAppropriatenessScore,
          key: "showMotiveAppropriatenessScore" as keyof T,
          rolesAllowed: [Roles.PROVIDER_CLAIMS_INSIGHTS],
          title: "Show Motive Appropriateness Tiers",
        },
        {
          dependency: "showPciData",
          hidden: optionsConfig.isZipProject || optionsConfig.hidePciData ||
            !optionsConfig.showPciDataChecked || optionsConfig.hideCareJourneyScores,
          key: "showCareJourneyScores" as keyof T,
          rolesAllowed: [Roles.PROVIDER_CLAIMS_INSIGHTS],
          title: "Show CareJourney Scores",
        },
        {
          hidden: !optionsConfig.showNetworkIntegrityDashboard,
          key: "showNetworkIntegrityDashboard" as keyof T,
          title: "Show Network Integrity Dashboard",
        }
      ],
      title: "Display Options",
    },
    {
      fields: [
        {
          exclusionType: ProjectExclusionType.DeactivatedProvider,
          key: "excludeDeactivatedProvidersNetwork" as keyof T, // If you edit this key, update `EditDisplayOptions.tsx`
          title: "Exclude Deactivated Network Providers",
        },
        {
          exclusionType: ProjectExclusionType.DeactivatedFacility,
          key: "excludeDeactivatedFacilitiesNetwork" as keyof T, // If you edit this key, update `EditDisplayOptions.tsx`
          title: "Exclude Deactivated Network Facilities",
        },
        {
          exclusionType: ProjectExclusionType.InvalidNppesNpiFacility,
          key: "excludeInvalidNppesNpiFacilitiesNetwork" as keyof T, // If you edit this key, update `EditDisplayOptions.tsx`
          title: "Exclude Invalid NPPES NPIs Network Facilities",
        },
        {
          exclusionType: ProjectExclusionType.InvalidNppesNpiProvider,
          key: "excludeInvalidNppesNpiProvidersNetwork" as keyof T, // If you edit this key, update `EditDisplayOptions.tsx`
          title: "Exclude Invalid NPPES NPIs Network Providers",
        },
        {
          exclusionType: ProjectExclusionType.MissingNpiFacility,
          key: "excludeMissingNpiFacilitiesNetwork" as keyof T, // If you edit this key, update `EditDisplayOptions.tsx`
          title: "Exclude Missing NPIs Network Facilities",
        },
        {
          exclusionType: ProjectExclusionType.MissingNpiProvider,
          key: "excludeMissingNpiProvidersNetwork" as keyof T, // If you edit this key, update `EditDisplayOptions.tsx`
          title: "Exclude Missing NPIs Network Providers",
        },
        {
          exclusionType: ProjectExclusionType.MedicareOptOut,
          key: "excludeMedicareOptOutProvidersNetwork" as keyof T, // If you edit this key, update `EditDisplayOptions.tsx`
          rolesAllowed: [Roles.MEDICARE_OPT_OUTS],
          rolesDenied: [Roles.TOKEN_USER],
          title: "Exclude Medicare Opt-Out Network Providers",
        },
        {
          exclusionType: ProjectExclusionType.OIGExclusion,
          key: "excludeOIGExcludedProvidersNetwork" as keyof T, // If you edit this key, update `EditDisplayOptions.tsx`
          rolesAllowed: [Roles.OIG],
          rolesDenied: [Roles.TOKEN_USER],
          title: "Exclude OIG Excluded Network Providers",
        },
        {
          exclusionType: ProjectExclusionType.Precluded,
          key: "excludePrecludedProvidersNetwork" as keyof T, // If you edit this key, update `EditDisplayOptions.tsx`
          rolesAllowed: [Roles.PRECLUDED_PROVIDER_USER],
          rolesDenied: [Roles.TOKEN_USER],
          title: "Exclude Medicare Precluded Network Providers",
        },
      ],
      title: "Exclude Network Providers",
    },
    {
      fields: [
        {
          exclusionType: ProjectExclusionType.DeactivatedProvider,
          impact: true,
          key: "excludeDeactivatedProvidersImpact" as keyof T, // If you edit this key, update `EditDisplayOptions.tsx`
          title: "Exclude Deactivated Prospect/Market Providers",
        },
        {
          exclusionType: ProjectExclusionType.DeactivatedFacility,
          impact: true,
          key: "excludeDeactivatedFacilitiesImpact" as keyof T, // If you edit this key, update `EditDisplayOptions.tsx`
          title: "Exclude Deactivated Prospect/Market Facilities",
        },
        {
          exclusionType: ProjectExclusionType.InvalidNppesNpiFacility,
          impact: true,
          key: "excludeInvalidNppesNpiFacilitiesImpact" as keyof T, // If you edit this key, update `EditDisplayOptions.tsx`
          title: "Exclude Invalid NPPES NPIs Prospect/Market Facilities",
        },
        {
          exclusionType: ProjectExclusionType.InvalidNppesNpiProvider,
          impact: true,
          key: "excludeInvalidNppesNpiProvidersImpact" as keyof T, // If you edit this key, update `EditDisplayOptions.tsx`
          title: "Exclude Invalid NPPES NPIs Prospect/Market Providers",
        },
        {
          exclusionType: ProjectExclusionType.MissingNpiFacility,
          impact: true,
          key: "excludeMissingNpiFacilitiesImpact" as keyof T, // If you edit this key, update `EditDisplayOptions.tsx`
          title: "Exclude Missing NPIs Prospect/Market Facilities",
        },
        {
          exclusionType: ProjectExclusionType.MissingNpiProvider,
          impact: true,
          key: "excludeMissingNpiProvidersImpact" as keyof T, // If you edit this key, update `EditDisplayOptions.tsx`
          title: "Exclude Missing NPIs Prospect/Market Providers",
        },
        {
          exclusionType: ProjectExclusionType.MedicareOptOut,
          impact: true,
          key: "excludeMedicareOptOutProvidersImpact" as keyof T, // If you edit this key, update `EditDisplayOptions.tsx`
          rolesAllowed: [Roles.MEDICARE_OPT_OUTS],
          rolesDenied: [Roles.TOKEN_USER],
          title: "Exclude Medicare Opt-Out Prospect/Market Providers",
        },
        {
          exclusionType: ProjectExclusionType.OIGExclusion,
          impact: true,
          key: "excludeOIGExcludedProvidersImpact" as keyof T, // If you edit this key, update `EditDisplayOptions.tsx`
          rolesAllowed: [Roles.OIG],
          rolesDenied: [Roles.TOKEN_USER],
          title: "Exclude OIG Excluded Prospect/Market Providers",
        },
        {
          exclusionType: ProjectExclusionType.Precluded,
          impact: true,
          key: "excludePrecludedProvidersImpact" as keyof T, // If you edit this key, update `EditDisplayOptions.tsx`
          rolesAllowed: [Roles.PRECLUDED_PROVIDER_USER],
          rolesDenied: [Roles.TOKEN_USER],
          title: "Exclude Medicare Precluded Prospect/Market Providers",
        },
      ],
      title: "Exclude Prospect/Market Providers",
    },
    {
      fields: [
        {
          key: "showAffiliationsInsights" as keyof T,
          rolesAllowed: [Roles.PROVIDER_AFFILIATIONS_INSIGHTS],
          title: "Show Affiliation Insights",
        },
        {
          dependency: "showAffiliationsInsights",
          key: "displayOnNetworkAccordion" as keyof T,
          rolesAllowed: [Roles.PROVIDER_AFFILIATIONS_INSIGHTS],
          title: "Display on Network Accordion",
        },
      ],
      title: "Affiliations Insights"
    }
  ];

  // Disable options that are not supported for bulk project edit changes
  if (optionsConfig.disableUnsupportedOptions) {
    allOptions.forEach((group) => {
        group.fields = DisableUnsupportedBulkEditOptions(group.fields);
      });
    }
  

  const allowedOptions = () => {
    const { userRoles } = optionsConfig;
    const filteredOptions = allOptions.map(group => {
      /**
       * 1. Keep the field if the field exists and
       * 2. the field isn't listed as hidden and
       * 3. the field doesn't list any allowed roles (i.e. all are allowed), or
       * 4a. the field does list allowed roles and the user has 1+ of those and
       * 4b1. the field has denied roles and the user doesn't have any of those or
       * 4b2. the field doesn't have denied roles listed (i.e. no roles are denied)
       */
      const filteredFields = group.fields.filter(
        (field) =>
          !!field &&
          !field.hidden &&
          (
            !field?.rolesAllowed ||
            (userRoles.some((r) => field?.rolesAllowed?.includes(r)) &&
              (field?.rolesDenied?.every((d) => !userRoles.includes(d)) ||
                !field.rolesDenied))
          )
      );

      if (optionsConfig.checkboxRefs) {
        filteredFields.forEach((ff) => {
          ff.ref = optionsConfig.checkboxRefs[ff.key];
        });
      }

      group.fields = filteredFields;
      return group;
    }).filter((group) => group.fields.length > 0);

    return filteredOptions;
  };

  return allowedOptions();
}

export default getOptions;
